import { useEffect, useState, useCallback } from "react";
import {
  Text,
  VStack,
  Spinner,
  Button,
  Box,
  Stack,
  HStack,
  Input,
  useBreakpointValue,
  Icon,
  Divider,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import { KYDEvent as IKYDEvent } from "@common/interfaces/KYDEvent";
import { Wallet } from "@common/interfaces/KYDUser";
import md5 from "md5";
import { analytics, AUTH_STATES, ModalType, showSuccessToast } from "../utils";
import { useNavigate, useParams } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { useForm } from "react-hook-form";
import actions from "../actions";
import { useDialog } from "../Common/Dialog";

type FormInputs = {
  first_name: string;
  last_name: string;
  email: string;
};

function KYDEventWallet({
  kydEvent,
  authState,
  wallet,
  loading,
  setLoading,
  fetchWallet,
  setModalType,
  onSignIn,
}: {
  kydEvent: IKYDEvent;
  authState: AUTH_STATES;
  wallet: Wallet | null;
  fetchWallet: (event_id: string) => void;
  setModalType: (_type: ModalType, _content?: string) => void;
  onSignIn: () => void;
  loading: Record<string, boolean>;
  setLoading: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}) {
  const dialog = useDialog();
  const navigate = useNavigate();
  const { event_id } = useParams();
  const toast = useToast();
  const shareCTA = useBreakpointValue({ base: "Share", md: "Copy Link" });
  const qrCodeSize = useBreakpointValue({
    base: 200,
    md: 260,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();

  const { onCopy } = useClipboard(wallet?.share_link || "");

  const rotateQRCode = useCallback((_wallet: Wallet) => {
    const timestamp = new Date().toISOString();
    const formatString = `${_wallet.token.id}/${timestamp}`;
    const checksumSlice = 10;
    const checksum = md5(formatString).slice(0, checksumSlice);
    const withChecksum = `${formatString}/${checksum}/${checksumSlice}`;
    return withChecksum;
  }, []);

  const [qrCodeContent, setQRCodeContent] = useState<string | null>(
    wallet ? rotateQRCode(wallet) : null
  );

  useEffect(() => {
    if (wallet) {
      const interval = setInterval(() => {
        const content = rotateQRCode(wallet);
        setQRCodeContent(content);
      }, 5000);

      if (!qrCodeContent) {
        const content = rotateQRCode(wallet);
        setQRCodeContent(content);
      }

      return () => clearInterval(interval);
    }
  }, [wallet, qrCodeContent, rotateQRCode]);

  const onUpdateAttendeeInformation = async (data: any) => {
    analytics.t("wallet: complete registration");
    setLoading((prevLoading: any) => {
      prevLoading.profile = true;
      return prevLoading;
    });
    try {
      await actions.USER.updateProfile(data);
      fetchWallet(event_id!);
    } catch (err) {
      //@ts-ignore
      dialog({ text: err.message });
    }
    setLoading((prevLoading: any) => {
      prevLoading.profile = false;
      return prevLoading;
    });
  };

  /*useEffect(() => {
    if (queryParams && queryParams.get("mode") === "tickets") {
      if (authState === AUTH_STATES.AUTHENTICATED) {
        setModalType(ModalType.SHOW_WALLET);
      } else {
        setModalType(ModalType.SIGN_IN_PHONE_NUMBER);
        setShowTicketsAfterAuth(true);
      }

      queryParams.delete("mode");
      const newUrl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?" +
        queryParams.toString();
      window.history.pushState({ path: newUrl }, "", newUrl);
    }
  }, [authState, modalType, queryParams, setModalType]);*/

  const renderAttendeeInformation = () => (
    <VStack w="100%" spacing={4} p={4} bg="gray.100" rounded={"lg"}>
      <Text w="100%" fontSize={"xl"} fontWeight="bold">
        {"Attendee Information"}
      </Text>
      <Stack w="100%">
        <Stack w="100%" spacing={4} direction={["column", null, "row"]}>
          <Stack w={["100%", null, "50%"]} spacing={0}>
            <Text fontSize={"sm"}>First Name *</Text>
            <Input
              h="40px"
              {...register("first_name", {
                required: "First name required",
              })}
              placeholder="First Name"
              bg="white"
            />

            {errors.first_name && (
              <Text fontSize={"xs"} color="red.500">
                {errors.first_name.message}
              </Text>
            )}
          </Stack>
          <Stack w={["100%", null, "50%"]} spacing={0}>
            <Text fontSize={"sm"}>Last Name *</Text>
            <Input
              h="40px"
              {...register("last_name", {
                required: "Last name required",
              })}
              placeholder="Last Name"
              bg="white"
            />
            {errors.last_name && (
              <Text fontSize={"xs"} color="red.500">
                {errors.last_name.message}
              </Text>
            )}
          </Stack>
        </Stack>
        <Stack w="100%" spacing={4} direction={["column", null, "row"]}>
          <Stack w="100%" spacing={0}>
            <Text fontSize={"sm"}>Email *</Text>
            <Input
              h="40px"
              {...register("email", {
                required: "Email required",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Invalid email format",
                },
              })}
              placeholder="Email"
              bg="white"
            />
            {errors.email && (
              <Text fontSize={"xs"} color="red.500">
                {errors.email.message}
              </Text>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Button
        onClick={handleSubmit(onUpdateAttendeeInformation)}
        variant="kydDark"
        w="100%"
        isLoading={loading.profile}
      >
        Submit
      </Button>
    </VStack>
  );

  /*useEffect(() => {
    if (
      qrCodeContent &&
      (modalType === ModalType.SHOW_WALLET || showTicketsAfterAuth)
    ) {
      setModalType(ModalType.SHOW_WALLET, qrCodeContent || "");
      setShowTicketsAfterAuth(false);
    }
  }, [qrCodeContent, modalType, setModalType, showTicketsAfterAuth]);*/

  const renderTickets = () => (
    <Stack
      flexDir={["column-reverse", null, "row"]}
      bg="gray.100"
      rounded={"lg"}
      w="100%"
      justify={["top", null, "space-between"]}
      align="top"
      wrap={"wrap"}
      p={4}
      spacing={6}
    >
      <Stack mt={{ base: "10px", md: "inherit" }} textAlign={"left"}>
        <Stack spacing={0}>
          <Text color="gray.600" fontWeight={"medium"}>
            {kydEvent.display_mode === "normal" ? "Tickets:" : "RSVPs:"}
          </Text>
          <Stack spacing={0} fontSize={"sm"}>
            {wallet?.summary_items.map((i) => (
              <Text key={i.name}>
                {i.quantity} x {i.name}
              </Text>
            ))}
          </Stack>

          <HStack>
            <a target={"_blank"} rel="noreferrer" href={wallet?.explorer_link}>
              <HStack>
                <Text color="gray.500" fontSize={"xs"}>
                  View On Aptos
                </Text>
                <Icon color="gray.500" as={FaExternalLinkAlt} fontSize="xs" />
              </HStack>
            </a>
          </HStack>
        </Stack>

        {kydEvent.display_mode === "normal" ? (
          <Stack spacing={0}>
            <Text color="gray.600" fontWeight={"medium"}>
              Purchases:
            </Text>
            <Stack spacing={0} fontSize={"sm"}>
              {wallet?.purchase_dates.map((i) => (
                <Stack spacing={0} key={i.date}>
                  <Text>{i.date}</Text>

                  {i.receipt_url ? (
                    <a
                      style={{
                        fontSize: "smaller",
                        textDecoration: "underline",
                      }}
                      href={i.receipt_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download Receipt
                    </a>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : null}

        {wallet?.share_link ? (
          <Stack py={4}>
            <Text fontWeight={"bold"} color={"#4022FB"}>
              Share with friends
            </Text>
            <HStack w="100%">
              <Text
                fontSize={"sm"}
                fontWeight={"bold"}
                borderColor={"gray.300"}
                cursor={"pointer"}
                borderBottom={{ base: "0px", md: "1px" }}
                onClick={async () => {
                  analytics.t("event: copied share link", {
                    location: "wallet",
                  });
                  if (window && window.navigator && window.navigator.share) {
                    try {
                      await window.navigator.share({
                        title: kydEvent.name,
                        url: wallet.share_link,
                      });
                      console.log("Data was shared successfully");
                    } catch (err) {
                      //@ts-ignore
                      console.error("Share failed:", err.message);
                    }
                  } else {
                    showSuccessToast(toast, "✅ Link copied!");
                    onCopy();
                  }
                }}
              >
                {wallet.share_link?.split("://").pop()}
              </Text>
              <Text
                borderWidth={{ base: "1px", md: "0px" }}
                borderColor="black"
                bg="none"
                fontSize={"sm"}
                px={2}
                rounded={"md"}
                color={{ base: "black", md: "blue.500" }}
                fontWeight={"bold"}
                cursor={"pointer"}
                onClick={async () => {
                  analytics.t("event: copied share link", {
                    location: "wallet",
                  });
                  if (window && window.navigator && window.navigator.share) {
                    try {
                      await window.navigator.share({
                        title: kydEvent.name,
                        url: wallet.share_link,
                      });
                    } catch (err) {
                      //@ts-ignore
                      console.error("Share failed:", err.message);
                    }
                  } else {
                    showSuccessToast(toast, "✅ Link copied!");
                    onCopy();
                  }
                }}
              >
                {shareCTA}
              </Text>
            </HStack>
          </Stack>
        ) : null}
      </Stack>
      <VStack spacing={4}>
        {qrCodeContent && (
          <Box
            p={6}
            bg="white"
            rounded={"lg"}
            borderWidth={"1px"}
            borderColor={"gray.300"}
          >
            <QRCodeSVG
              value={qrCodeContent}
              size={qrCodeSize}
              mode="L"
              imageSettings={{
                src: "https://content.kydlabs.com/system/crown.png",
                x: undefined,
                y: undefined,
                height: 40,
                width: 40,
                excavate: true,
              }}
            />
          </Box>
        )}
        {kydEvent.display_mode === "normal" ? (
          <Button
            onClick={() => {
              analytics.t("wallet: clicked transfer tickets");
              setModalType(ModalType.TRANSFER_TICKETS_PHONE);
            }}
            color="white"
            bgGradient={"linear(to-b, #F56565, #ED64A6)"}
            _hover={{ bg: "linear(to-b, #F56565, #ED64A6)" }}
          >
            Transfer Tickets
          </Button>
        ) : null}
        <Divider
          display={{ base: "inherit", md: "none" }}
          borderColor={"gray.300"}
        />
      </VStack>
    </Stack>
  );

  const renderWallet = () => {
    if (loading.wallet) {
      return (
        <VStack p={4} w="100%">
          <VStack>
            <Text>Loading tickets...</Text>
            <Spinner />
          </VStack>
        </VStack>
      );
    } else if (loading.wallet_silent) {
      if (wallet?.tickets.length === 0) {
        return (
          <VStack p={4} w="100%">
            <VStack>
              <Text>Loading tickets...</Text>
              <Spinner />
            </VStack>
          </VStack>
        );
      } else {
        return renderTickets();
      }
    } else if (wallet) {
      if (wallet.onboarding_status === "EMAIL_REQUIRED") {
        return renderAttendeeInformation();
      } else if (wallet.tickets.length > 0) {
        return renderTickets();
      } else {
        return renderNoPurchases();
      }
    }
  };

  const renderNoPurchases = () => (
    <VStack>
      <Text maxW={"400px"}>
        You haven't purchased any tickets yet! Head back to the event page to
        grab your tickets.
      </Text>
      <Button
        maxW={"200px"}
        variant={"kydDark"}
        onClick={() => {
          analytics.t("wallet: go back");
          const searchParams = new URLSearchParams(window.location.search);
          navigate(`/e/${event_id}?${searchParams.toString()}`);
        }}
      >
        Get Tickets
      </Button>
    </VStack>
  );

  const renderSignIn = () => (
    <VStack color={{ base: "white", md: "black" }}>
      <Text>Please sign in to view your tickets 🎟</Text>
      <Button
        w={"100%"}
        onClick={() => {
          analytics.t("wallet: sign in");
          onSignIn();
        }}
        bg={{ base: "white", md: "black" }}
        color={{ base: "black", md: "white" }}
        _hover={{ bg: { base: "gray.100", md: "gray.900" } }}
      >
        Sign In
      </Button>
    </VStack>
  );

  return (
    <VStack w="100%" p={{ base: "2", md: "0" }}>
      <VStack
        w="100%"
        roundedTop={{
          base: authState === AUTH_STATES.NEEDS_AUTH ? "none" : "lg",
          md: "lg",
        }}
        roundedBottom={{ base: "lg", md: "none" }}
        borderTopWidth={{ base: "1px", md: "none" }}
        borderTopColor={{ base: "white", md: "transparent" }}
        bg={{
          base: authState === AUTH_STATES.NEEDS_AUTH ? "transparent" : "white",
          md: "white",
        }}
        p={4}
      >
        <Stack spacing={1} w="100%">
          <HStack justify={"space-between"}>
            <Text
              color={{
                base: authState === AUTH_STATES.NEEDS_AUTH ? "white" : "black",
                md: "black",
              }}
              w="100%"
              textAlign={"left"}
              fontWeight={"bold"}
              fontSize="xl"
            >
              {kydEvent.display_mode === "normal" ? "My Tickets" : "My RSVPs"}
            </Text>
            {loading.wallet_silent && wallet && wallet.tickets.length > 0 && (
              <Spinner />
            )}
          </HStack>

          {wallet &&
          wallet.tickets.length > 0 &&
          wallet.onboarding_status === "EMAIL_REQUIRED" ? (
            <Text
              color={{
                base: authState === AUTH_STATES.NEEDS_AUTH ? "white" : "black",
                md: "black",
              }}
              maxW={["100%", null, "60%"]}
              w="100%"
              textAlign={"left"}
              fontSize="sm"
            >
              One more step! Enter your information below to receive your
              tickets 🎟️
            </Text>
          ) : null}
          {wallet &&
            wallet.tickets.length > 0 &&
            wallet.onboarding_status !== "EMAIL_REQUIRED" && (
              <Text
                color={{
                  base:
                    authState === AUTH_STATES.NEEDS_AUTH ? "white" : "black",
                  md: "black",
                }}
                maxW={["100%", null, "60%"]}
                w="100%"
                textAlign={"left"}
                fontSize="sm"
              >
                {kydEvent.display_mode === "normal"
                  ? `Please display the QR code below on your mobile phone for entry into ${
                      kydEvent?.name || " the event"
                    }`
                  : "Thank you for sharing you'll be attending. The QR code below is your RSVP confirmation for the host. This QR code does not guarantee entry."}
              </Text>
            )}
        </Stack>

        {authState === AUTH_STATES.AUTHENTICATED && renderWallet()}

        {authState === AUTH_STATES.NEEDS_AUTH && renderSignIn()}
      </VStack>
    </VStack>
  );
}

export default KYDEventWallet;
